import type { ReactNode } from "react";
import { SlideStatement } from "../../../SlideStatement";
import { SlideVerdict } from "../../../SlideVerdict";

interface Column {
  step: string;
  label: string;
  role: ReactNode;
  bullets: string[];
}

const COLUMNS: Column[] = [
  {
    step: "01",
    label: "Insights &\nRecommendations\nGenerated",
    role: (
      <>
        20+ sources, run through human-curated{" "}
        <span className="font-semibold text-slate-900">ASO Engines</span>.
      </>
    ),
    bullets: [
      "Recommendations, ranked by impact",
      "Per-market keyword & metadata gaps",
      "Screenshot, video & review fixes",
      "LLM-answer visibility (GEO)",
    ],
  },
  {
    step: "02",
    label: "Optimizations\nImplemented",
    role: (
      <>
        Ranked recommendations become a{" "}
        <span className="font-semibold text-slate-900">draft revision</span>.
      </>
    ),
    bullets: [
      "One revision covers metadata, keywords & creative",
      "You review, edit, approve \u2014 nothing ships unseen",
      "One-click publish to both stores",
    ],
  },
  {
    step: "03",
    label: "Impact\nMonitored",
    role: (
      <>
        Source-of-truth results from{" "}
        <span className="font-semibold text-slate-900">
          the stores themselves
        </span>
        .
      </>
    ),
    bullets: [
      "Impressions, conversion & installs, per revision",
      "Every delta attributed to the revision that shipped it",
      "Results feed the next audit \u2014 the loop closes",
    ],
  },
];

export default function TheSolution() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-0">
      <SlideStatement eyebrow="The Solution">
        <div>
          <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
            Successful mobile apps require continuous on-trend ASO.
          </h1>
          {/* Restatement, not consequence — it says what the thing IS, so it
              sits above the rule with the claim (see slide 3). */}
          <p className="mt-4 text-lg font-medium italic tracking-tight text-slate-600 md:mt-5 md:text-2xl">
            Not another ASO dashboard. Not a thin AI wrapper calling itself
            SaaS.
          </p>
        </div>
      </SlideStatement>

      {/* Below the rule — the loop, named and then walked. The caption is the
          same phrase the Moat slide uses for it, so the two slides describe
          one thing rather than two. The section centres in whatever height
          C+D+E leaves after the verdict bar. */}
      <div className="flex flex-col justify-center md:min-h-0 md:flex-1 md:pt-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 md:text-sm md:tracking-[0.2em]">
          Insights → Actions → Impact — the closed loop
        </p>
        <div className="mt-3 grid grid-cols-1 items-start gap-4 md:mt-4 md:grid-cols-3 md:gap-6">
          {COLUMNS.map((col) => (
            <SolutionColumn key={col.label} {...col} />
          ))}
        </div>
      </div>

      <SlideVerdict>An always-active ASO Operating System.</SlideVerdict>
    </div>
  );
}

function SolutionColumn({ step, label, role, bullets }: Column) {
  return (
    <div className="flex flex-col rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200 md:p-6">
      <p className="text-xs font-bold tracking-[0.18em] text-slate-300 md:text-sm">
        {step}
      </p>
      <h3 className="mt-1 whitespace-pre-line text-xl font-bold leading-tight tracking-tight text-blue-600 md:text-2xl">
        {label}
      </h3>
      <p className="mt-2 text-sm leading-snug text-slate-600 md:text-base">
        {role}
      </p>
      <ul className="mt-3 space-y-1.5 border-t border-slate-100 pt-3 text-sm leading-snug text-slate-600 md:text-[15px]">
        {bullets.map((b) => (
          <li key={b} className="flex gap-2">
            <span aria-hidden="true" className="shrink-0 text-blue-600">
              →
            </span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
